"use client";

import { useState, useCallback } from "react";
import type { MouseEvent } from "react";
import Checkbox from "@/shared/ui/components/checkbox";
import Modal from "@/shared/ui/components/modal";
import Button from "@/shared/ui/components/button";
import PdfDocument from "@/shared/ui/components/pdf-document";
import { DocumentIcon } from "@/shared/ui/icons/document-icon";
import type { AppContext, LoanDocument } from "@/types";
import { LOCALIZED_ICON_TYPE } from "@/types";
import * as events from "@/events";
import { useCosmosClient } from "@/services/cosmos";
import { emitDocumentTracking } from "@/shared/tracking/analytics";
import { getDocumentListItemLabel } from "@/lib/checkout-documents";
import {
  agreeFinancingAgreementsLabelKey,
  financingDocumentsTitleKey,
} from "@/lib/payment-option";

interface DocumentsModalNewProps {
  ctx: AppContext;
  isOpen: boolean;
  isClosing: boolean;
  onClose: () => void;
  onAgree: (agreed: boolean) => void;
  isAgreed: boolean;
  /** When set, only these items are listed (e.g. non–Key Facts documents). Defaults to all session documents. */
  documentsList?: LoanDocument[] | null;
  /** UAE BNPL+ uses financing document/agreement copy instead of Murabaha. */
  isUaeBnplPlus?: boolean;
}

export function DocumentsModalNew({
  isOpen,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isClosing: _isClosing,
  onClose,
  onAgree,
  isAgreed,
  documentsList,
  isUaeBnplPlus = false,
  ctx: { t, sendEvent, localizedIcon, session, rtl },
}: DocumentsModalNewProps) {
  const documentLabel = (documentItemType: string) =>
    getDocumentListItemLabel(documentItemType, t, session.order.countryCode);
  const documents = documentsList ?? session.documents ?? [];

  const [isTermsDetailOpen, setIsTermsDetailOpen] = useState<{
    open: boolean;
    document: LoanDocument | null;
    documentUrl: string | null;
  }>({
    open: false,
    document: null,
    documentUrl: null,
  });
  const [bottomSheetAgreed, setBottomSheetAgreed] = useState(isAgreed);
  const cosmosClient = useCosmosClient();

  const handleOpenTermsDetail = useCallback(
    async (doc: LoanDocument) => {
      try {
        const response = await cosmosClient.getDocument(doc.id);
        const documentUrl = response?.url || doc.url || "";
        setIsTermsDetailOpen({ open: true, document: doc, documentUrl });
      } catch (error) {
        console.error("Error fetching document:", error);
        setIsTermsDetailOpen({
          open: true,
          document: doc,
          documentUrl: doc.url || null,
        });
      }
    },
    [cosmosClient],
  );

  const handleCloseTermsDetail = useCallback(() => {
    setIsTermsDetailOpen({ open: false, document: null, documentUrl: null });
  }, []);

  const handleClosePdfModal = useCallback(
    (close: () => void) => {
      sendEvent(events.CLOSE_MODAL_CLICKED, {
        modal_name: "finance_document",
      });
      close();
    },
    [sendEvent],
  );

  const handleDocumentClick = useCallback(
    (doc: LoanDocument) => {
      sendEvent(events.FINANCE_DOCUMENT_CLICKED, {
        document_id: doc.id,
        document_type: doc.documentItemType,
        document_url: doc.url,
      });
      emitDocumentTracking("finance_document_opened", session, {
        document_id: doc.id,
        document_type: doc.documentItemType,
      });
      handleOpenTermsDetail(doc).catch((err) =>
        console.error("Error opening document:", err),
      );
    },
    [sendEvent, session, handleOpenTermsDetail],
  );

  const handleAgreeChange = useCallback(
    (newValue: boolean) => {
      setBottomSheetAgreed(newValue);
      onAgree(newValue);
      emitDocumentTracking(
        newValue ? "terms_agreed" : "terms_disagreed",
        session,
      );
      if (newValue) {
        sendEvent(events.AGREE_TO_FINANCE_TERMS_CLICKED);
      }
    },
    [onAgree, sendEvent, session],
  );

  const handleAgreeToggle = useCallback(() => {
    handleAgreeChange(!bottomSheetAgreed);
  }, [bottomSheetAgreed, handleAgreeChange]);

  const handleAgreeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleAgreeToggle();
      }
    },
    [handleAgreeToggle],
  );

  const handlePdfGoBackClick = useCallback(
    (close: () => void) => (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      handleClosePdfModal(close);
    },
    [handleClosePdfModal],
  );

  if (!isOpen) return null;

  const documentUrlIsValid = Boolean(isTermsDetailOpen.documentUrl);

  return (
    <>
      <Modal onClose={onClose} showCloseButton>
        <div
          className="p-6 pt-12 space-y-6"
          dir={rtl ? "rtl" : "ltr"}
          data-testid="documents-modal"
        >
          <h3
            className={`text-xl font-semibold text-[#16181D] ${
              rtl ? "text-right" : "text-left"
            }`}
          >
            {t(financingDocumentsTitleKey(isUaeBnplPlus))}
          </h3>

          <div className="space-y-0">
            {documents.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => handleDocumentClick(doc)}
                data-testid={`document-item-${doc.id}`}
                className={`flex items-center gap-3 w-full py-4 border-b border-[#E5E7EB] last:border-b-0 text-[#16181D] font-medium hover:bg-[#F4F6F9] transition-colors ${
                  rtl ? "flex-row-reverse text-right" : "text-left"
                }`}
              >
                <DocumentIcon />
                <span className="flex-1">
                  {documentLabel(doc.documentItemType)}
                </span>
                <span className="text-[#7E82AD]">
                  {localizedIcon(LOCALIZED_ICON_TYPE.CHEVRON_FORWARD)}
                </span>
              </button>
            ))}
          </div>

          <h3
            className={`text-[16px] text-[#16181D] font-medium ${
              rtl ? "text-right" : "text-left"
            }`}
          >
            {t("documents.agree_here")}
          </h3>

          <div
            role="button"
            tabIndex={0}
            onKeyDown={handleAgreeKeyDown}
            onClick={handleAgreeToggle}
            className={`flex items-center gap-3 w-full p-4 bg-[#F4F6F9] rounded-xl cursor-pointer ${
              rtl ? "flex-row-reverse" : ""
            }`}
            data-testid="documents-modal-agree-container"
          >
            <span
              data-testid="documents-modal-agree-label"
              className={`flex-1 text-sm font-medium text-[#16181D] select-none ${
                rtl ? "text-right" : "text-left"
              }`}
            >
              {t(agreeFinancingAgreementsLabelKey(isUaeBnplPlus))}
            </span>
            <Checkbox
              value={bottomSheetAgreed}
              onChange={handleAgreeChange}
              data-testid="documents-modal-agree-checkbox"
            />
          </div>
        </div>
      </Modal>

      {isTermsDetailOpen.open && (
        <Modal
          onClose={handleCloseTermsDetail}
          showCloseButton
          full
          stickyFooter={(close) => (
            <Button
              size="large"
              className="w-full"
              data-testid="document-pdf-go-back-button"
              onClick={handlePdfGoBackClick(close)}
            >
              {t("documents.go_back")}
            </Button>
          )}
        >
          <div className="flex flex-col flex-1 min-h-0 relative">
            <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 md:px-6 pt-20 pb-24">
              {documentUrlIsValid ? (
                <PdfDocument
                  url={isTermsDetailOpen.documentUrl ?? ""}
                  className="w-full"
                />
              ) : (
                <div className="flex items-center justify-center min-h-[400px] text-gray-500">
                  {t("documents.unavailable")}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
